import React, { useContext, useEffect, useState } from "react";
import useStyles from "./styles";
import { __t } from "@core/translation/translation";
import { Grid } from "@material-ui/core";
import Typography from "@components/Typography";
import Button from "@components/Button";
import PriceFormat from "@components/PriceFormat";
import { CURRENCY } from "@core/utils/consts";
import { StatsContext } from "@context/statsContext";
import order from "@api/order";
import { notification } from "@core/helper/notificationHelper";
import { MarketContext } from "@context/MarketContext";
import PriceTextField from "../../PriceTextField";
import clsx from "clsx";
import { Link } from "react-router-dom";
import {
  returnCurrencyScale,
  returnMarketScale,
} from "@features/MarketOrder/components/MarketCard/SelectDigitalCurrency/Functions/scale";
import UnitPriceTooltip from "@components/UnitPriceTooltip";
import priceFormatString from "@core/helper/priceFormatString";
import ToFixed from "@core/helper/ToFixed";
import { ConstantsContext } from "@context/constantsContext";
import p2e from "@core/helper/convertPersianToEnglishNumber";

const initialFValues = {
  unitPrice: "",
  size: "",
  total: "",
};

const CustomInput = ({
  type,
  FirstCurrencyUnit,
  SecondCurrencyUnit,
  currencyName,
  currencyUnitName,
  walletInventory,
}) => {
  const classes = useStyles();
  const [values, setValues] = useState(initialFValues);
  const [errors, setErrors] = useState({});
  const { statsState } = useContext(StatsContext);
  const market = useContext(MarketContext);
  const [changeStateTotal, setChangeStateTotal] = useState(false);
  const [changeStateSize, setChangeStateSize] = useState(false);
  const [dontChange, setDontChange] = useState(true);

  const constants = useContext(ConstantsContext);
  const currencyMarket = currencyName?.replace("-", "_");

  const FirstCurrencyScale = returnCurrencyScale(
    constants?.orderScaleMap,
    FirstCurrencyUnit
  );
  const SecondCurrencyScale = returnMarketScale(
    constants?.marketOrderPriceScaleMap,
    currencyMarket
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value,
    });
    validate({ [name]: value });
  };

  useEffect(() => {
    if (String(values?.unitPrice)?.match(/\b0\d+/g)) {
      setValues((prevValues) => ({
        ...prevValues,
        unitPrice: values.unitPrice?.replace(/^0\d+/, ""),
      }));
    }
    if (dontChange) {
      if (!!values.size && !!values.unitPrice) {
        setValues((prevValues) => ({
          ...prevValues,
          total: `${(
            parseFloat(values.size) * parseFloat(values.unitPrice)
          ).toFixed(SecondCurrencyScale)}`,
        }));
      } else if (!!values.total && !!values.unitPrice) {
        setValues((prevValues) => ({
          ...prevValues,
          size: `${(
            parseFloat(values.total) / parseFloat(values.unitPrice)
          ).toFixed(FirstCurrencyScale)}`,
        }));
      } else if (values.unitPrice === "" && values.size === "") {
        setValues((prevValues) => ({
          ...prevValues,
          total: "",
        }));
      }
    }
  }, [values.unitPrice]);

  useEffect(() => {
    if (values.size === ".") {
      setValues((prevValues) => ({
        ...prevValues,
        size: "0.",
      }));
      return;
    }
    if (String(values.size)?.match(/\b0\d+/g)) {
      setValues((prevValues) => ({
        ...prevValues,
        size: values.size?.replace(/^0\d+/, ""),
      }));
    }
    if (values.size === "NaN") {
      setValues({
        ...values,
        size: "",
      });
    }
    if (dontChange) {
      if (!!values.size && !!values.unitPrice && !changeStateTotal) {
        setValues({
          ...values,
          total: `${(
            parseFloat(values.size) * parseFloat(values.unitPrice)
          ).toFixed(SecondCurrencyScale)}`,
        });
      } else if (values.size === "") {
        setValues({
          ...values,
          total: "",
        });
      }

      setChangeStateTotal(false);
    }
  }, [values.size]);

  useEffect(() => {
    if (values.total?.match(/\b0\d+/g)) {
      setValues((prevValues) => ({
        ...prevValues,
        total: values.total?.replace(/^0\d+/, ""),
      }));
    }
    if (dontChange) {
      if (
        (!!values.size && !!values.unitPrice && !changeStateSize) ||
        (!!values.total && !!values.unitPrice && !values.size)
      ) {
        setChangeStateTotal(true);
        setValues((prevValues) => ({
          ...prevValues,
          size: `${(
            parseFloat(values.total) / parseFloat(values.unitPrice)
          ).toFixed(FirstCurrencyScale)}`,
        }));
      }
      setChangeStateSize(false);
    }
  }, [values.total]);

  useEffect(() => {
    if (market.orderBookClick && type === market.orderType) {
      setDontChange(false);
      market.setOrderBookClick(false);
      setValues(market.selectPriceState);
    }
    if (!market.orderBookClick && !dontChange) {
      setDontChange(true);
    }
  }, [market.orderBookClick]);

  const validate = (fieldValues = values) => {
    let temp = { ...errors };

    if (type === "sell") {
      if ("size" in fieldValues) {
        temp.size =
          fieldValues.size <= walletInventory ? "" : "This field is required.";
      }
    } else if (type === "buy") {
      if ("total" in fieldValues)
        temp.total =
          fieldValues.total <= walletInventory ? "" : "This field is required.";
    }
    setErrors({
      ...temp,
    });
    if (fieldValues == values) return Object.values(temp).every((x) => x == "");
  };

  const resetForm = () => {
    setValues(initialFValues);
    setErrors({});
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      if (
        SecondCurrencyUnit === "IRT" &&
        parseFloat(values.total) < constants?.minOrder?.valueMap?.IRT
      ) {
        notification(
          "",
          "danger",
          __t("The minimum transaction value is {price} {unit}", {
            price: priceFormatString(constants?.minOrder?.valueMap?.IRT),
            unit: __t("toman"),
          })
        );
      } else if (
        SecondCurrencyUnit === "USDT" &&
        parseFloat(values.total) < constants?.minOrder?.valueMap?.USDT
      ) {
        notification(
          "",
          "danger",
          __t("The minimum transaction value is {price} {unit}", {
            price: priceFormatString(constants?.minOrder?.valueMap?.USDT),
            unit: __t("tether"),
          })
        );
      } else {
        order
          .postOrder({
            payload: {
              currencyName: FirstCurrencyUnit,
              marketName: currencyName?.replace("-", "_"),
              side: type.toUpperCase(),
              size: p2e(values.size),
              type: "LIMIT",
              unitPrice: p2e(values.unitPrice),
            },
          })
          .then((data) => {
            notification("", "success", data?.message);
            resetForm();
            market.setForceUpdate(!market.forceUpdate);
          })
          .catch((data) => {
            notification(
              "",
              "danger",
              !!data && !!data?.message ? data?.message : "SERVER ERROR"
            );
          });
      }
    }
  };

  const handleClickPrice = (value) => {
    setValues((prevValues) => ({
      ...prevValues,
      unitPrice:
        type === "buy"
          ? String(
              statsState?.stats[currencyName?.replace("-", "_")]?.bestSellPrice
            )
          : String(
              statsState?.stats[currencyName?.replace("-", "_")]?.bestBuyPrice
            ),
    }));
  };

  const handleClickInventory = (walletInventory) => {
    if (type === "buy") {
      setValues((prevValues) => ({
        ...prevValues,
        total: String(walletInventory),
      }));
      setChangeStateTotal(true);
    } else if (type === "sell") {
      setValues((prevValues) => ({
        ...prevValues,
        size: String(walletInventory),
      }));
      setChangeStateSize(true);
    }
  };

  return (
    <Grid item container direction="column" className={classes.container}>
      <Grid
        item
        container
        direction="row"
        justify="space-between"
        alignItems="center"
        className={classes.inventoryContainer}
      >
        <Grid item>
          {errors.total || errors.size ? (
            <div className={classes.errorContainer}>
              <span
                className={clsx("icon-alert-triangle", classes.errorIcon)}
              />
              <Typography
                variant="3xs"
                color="red2.main"
                className={classes.margin}
              >
                {__t("Insufficient inventory")}
              </Typography>
              <Link to={`/wallets/${currencyName}/deposit`}>
                <Typography
                  variant="3xs"
                  color="primary.main"
                  className={classes.depositLink}
                >
                  {__t("Charge wallet")}
                </Typography>
              </Link>
            </div>
          ) : (
            <Typography variant="3xs" color="secondary.tint40">
              {__t("Inventory")}
            </Typography>
          )}
        </Grid>

        <Grid item>
          <Typography variant="3xs" color="secondary.tint100">
            ({type === "buy" ? SecondCurrencyUnit : FirstCurrencyUnit}){" "}
          </Typography>
          <Typography variant="3xs" color="secondary.main" isBold>
            <PriceFormat
              price={walletInventory}
              onClick={() => handleClickInventory(walletInventory)}
              className={classes.priceText}
            />
          </Typography>
        </Grid>
      </Grid>
      <form noValidate onSubmit={onSubmit} autoComplete="off">
        <UnitPriceTooltip
          interactive
          placement="top"
          arrow
          title={
            <div
              className={classes.tootlipContainer}
              onClick={handleClickPrice}
            >
              <div>
                <Typography variant="3xs" color="secondary.tint40">
                  ({currencyUnitName}){" "}
                </Typography>
                <Typography variant="3xs" isBold>
                  <PriceFormat
                    price={
                      type === "buy"
                        ? statsState?.stats[currencyName?.replace("-", "_")]
                            ?.bestSellPrice
                        : statsState?.stats[currencyName?.replace("-", "_")]
                            ?.bestBuyPrice
                    }
                  />
                </Typography>
              </div>
              <Typography variant="3xs">{__t("Best Price")}</Typography>
            </div>
          }
        >
          <Grid item className={classes.marginBottom}>
            <PriceTextField
              name="unitPrice"
              text={__t("Price")}
              currencyName={SecondCurrencyUnit}
              value={values.unitPrice}
              handleChange={handleChange}
              error={errors.unitPrice}
              hover={true}
            />
          </Grid>
        </UnitPriceTooltip>
        <Grid item className={classes.marginBottom}>
          <PriceTextField
            name="size"
            text={__t("Digital Currency Amount")}
            currencyName={FirstCurrencyUnit}
            value={values.size}
            handleChange={handleChange}
            error={errors.size}
          />
        </Grid>
        <Grid item className={classes.marginBottom}>
          <PriceTextField
            name="total"
            text={__t("Total")}
            currencyName={SecondCurrencyUnit}
            value={values.total}
            handleChange={handleChange}
            error={errors.total}
          />
        </Grid>
        <Grid item>
          <Button
            color={type === "buy" ? "primary" : "red2"}
            fullWidth
            variant="contained"
            size="small"
            type="submit"
            disabled={
              errors.total ||
              errors.size ||
              !values.total ||
              !values.size ||
              !values.unitPrice ||
              values.total === "0"
                ? true
                : false
            }
          >
            <Typography variant="3xs">
              {type === "buy"
                ? __t("Buy {market}", {
                    market: CURRENCY?.TYPE[FirstCurrencyUnit]?.NAME,
                  })
                : __t("Sell {market}", {
                    market: CURRENCY?.TYPE[FirstCurrencyUnit]?.NAME,
                  })}
            </Typography>
          </Button>
        </Grid>
      </form>
    </Grid>
  );
};

export default CustomInput;
